import { useState } from "react";
import { useOffice } from "@/office/office-context";
import {
  STEP_SPECS,
  SOURCE_FILES,
  getStepBlocks,
  getStepFileRanges,
  type SourceFileKey,
} from "@/office/step-catalog";
import { SyntaxCode } from "@/office/SyntaxCode";

type TabKey = "step" | "file" | "state";

const TABS: { key: TabKey; label: string; hint: string }[] = [
  { key: "step", label: "本步代码", hint: "该操作对应的完整实现" },
  { key: "file", label: "所在文件", hint: "源码文件全貌与位置" },
  { key: "state", label: "状态快照", hint: "这一步之后的完整状态" },
];

export function CodePanel() {
  const office = useOffice();
  const step = office.activeStep;
  const [tab, setTab] = useState<TabKey>("step");
  const [pickedFile, setPickedFile] = useState<SourceFileKey | null>(null);
  const [copied, setCopied] = useState(false);

  if (!step) {
    return (
      <section className="flex h-full items-center justify-center bg-[#0c131b] text-[13px] text-[#63788c]">
        在左侧操作任意一步，这里会同步显示对应源码
      </section>
    );
  }

  const spec = STEP_SPECS[step.kind];
  const blocks = getStepBlocks(step.kind);
  const primaryFile = blocks[0]?.fileKey ?? "context";
  const ranges = getStepFileRanges(step.kind);
  const activeFile = pickedFile ?? primaryFile;
  const activeSource = SOURCE_FILES[activeFile].source;
  const activeRange = ranges.find((range) => range.fileKey === activeFile && range.start <= range.end);

  const headerLines = [
    `// 第 ${step.index} 步 · ${step.title}   ${step.time}`,
    `// 操作：${step.detail}`,
    `// ${spec.summary}`,
  ];
  const stepCode = [
    ...headerLines,
    "",
    ...blocks.flatMap((block) => [
      `// ── ${block.path} · ${block.caption} ──`,
      block.code,
      "",
    ]),
  ].join("\n");

  const totalLines = blocks.reduce((total, block) => total + block.code.split("\n").length, 0);
  const stateJson = JSON.stringify(step.snapshot, null, 2);

  const copyText =
    tab === "step" ? stepCode : tab === "file" ? activeSource : stateJson;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="flex h-full min-h-0 flex-col bg-[#0c131b] text-[#cfdae6]">
      <div className="shrink-0 border-b border-[#16222e] px-5 pb-3 pt-3.5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded bg-[#16303f] px-2 py-1 font-mono text-[11px] text-[#5fd0c2]">
            step {String(step.index).padStart(2, "0")}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[14px] font-medium text-[#e7eef6]">{step.title}</h2>
            <p className="mt-0.5 truncate font-mono text-[11.5px] text-[#7c93a8]">{step.detail}</p>
          </div>
          {office.replaying ? (
            <button
              type="button"
              onClick={() => office.selectStep(null)}
              className="shrink-0 rounded border border-[#f0b866]/40 bg-[#f0b866]/10 px-2 py-1 text-[11px] text-[#f0b866] transition-colors hover:bg-[#f0b866]/20"
            >
              回放中 · 返回最新
            </button>
          ) : (
            <span className="shrink-0 rounded border border-[#37d6a0]/30 bg-[#37d6a0]/10 px-2 py-1 text-[11px] text-[#5fe0b0]">
              实时同步
            </span>
          )}
        </div>

        <div className="mt-3 flex items-center gap-2 overflow-hidden text-[11px] text-[#6c8296]">
          <span className="truncate font-mono text-[#8fa8bd]">{blocks[0]?.path ?? "src/office/office-context.tsx"}</span>
          <span className="text-[#2d4050]">·</span>
          <span>{blocks.length} 个代码块</span>
          <span className="text-[#2d4050]">·</span>
          <span>{totalLines} 行</span>
          <span className="text-[#2d4050]">·</span>
          <span>{step.module === "doc" ? "文字模块" : "表格模块"}</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1 border-b border-[#16222e] px-3">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            title={item.hint}
            onClick={() => setTab(item.key)}
            className={`relative h-9 px-2.5 text-[12px] transition-colors ${
              tab === item.key ? "text-[#e7eef6]" : "text-[#6e8599] hover:text-[#a9bccd]"
            }`}
          >
            {item.label}
            {tab === item.key ? <span className="absolute inset-x-2 -bottom-px h-[2px] rounded bg-[#4f8cff]" /> : null}
          </button>
        ))}
        <span className="ml-auto hidden pr-2 text-[10.5px] text-[#4e6478] xl:block">源码由 Vite?raw 从运行中的文件读取</span>
      </div>

      {tab === "file" ? (
        <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-[#16222e] bg-[#0a1118] px-3 py-1.5">
          {(Object.keys(SOURCE_FILES) as SourceFileKey[]).map((key) => {
            const isActive = key === activeFile;
            const touched = ranges.some((range) => range.fileKey === key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => setPickedFile(key)}
                className={`flex shrink-0 items-center gap-1.5 rounded px-2 py-1 font-mono text-[11px] transition-colors ${
                  isActive ? "bg-[#17242f] text-[#cfe3f5]" : "text-[#69809a] hover:bg-[#121c26]"
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${touched ? "bg-[#37d6a0]" : "bg-[#2b3d4d]"}`} />
                {SOURCE_FILES[key].short}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
        {tab === "step" ? (
          <div className="space-y-4">
            <div className="rounded border border-[#1a2836] bg-[#0f1922] px-3 py-2 font-mono text-[11.5px] leading-relaxed text-[#6f8ba2]">
              {headerLines.map((line, index) => (
                <div key={index} className="whitespace-pre-wrap">
                  {line}
                </div>
              ))}
            </div>
            {blocks.map((block) => (
              <div key={`${block.fileKey}-${block.caption}`} className="overflow-hidden rounded border border-[#1a2836]">
                <div className="flex items-center gap-2 border-b border-[#1a2836] bg-[#101b25] px-3 py-1.5">
                  <span className="font-mono text-[11px] text-[#7fb3ff]">{block.path}</span>
                  <span className="text-[#2d4050]">/</span>
                  <span className="truncate text-[11px] text-[#7c93a8]">{block.caption}</span>
                  <span className="ml-auto shrink-0 font-mono text-[10.5px] text-[#4e6478]">
                    L{block.startLine}–{block.startLine + block.code.split("\n").length - 1}
                  </span>
                </div>
                <SyntaxCode code={block.code} startLine={block.startLine} />
              </div>
            ))}
          </div>
        ) : null}

        {tab === "file" ? (
          <div className="overflow-hidden rounded border border-[#1a2836]">
            <div className="flex items-center gap-2 border-b border-[#1a2836] bg-[#101b25] px-3 py-1.5">
              <span className="font-mono text-[11px] text-[#7fb3ff]">{SOURCE_FILES[activeFile].path}</span>
              <span className="ml-auto font-mono text-[10.5px] text-[#4e6478]">
                {activeSource.split("\n").length} 行
              </span>
            </div>
            <SyntaxCode code={activeSource} highlightRange={activeRange ? { start: activeRange.start, end: activeRange.end } : undefined} />
          </div>
        ) : null}

        {tab === "state" ? (
          <div className="overflow-hidden rounded border border-[#1a2836]">
            <div className="flex items-center gap-2 border-b border-[#1a2836] bg-[#101b25] px-3 py-1.5">
              <span className="font-mono text-[11px] text-[#7fb3ff]">office-state.json</span>
              <span className="ml-auto text-[10.5px] text-[#4e6478]">第 {step.index} 步执行后的完整状态</span>
            </div>
            <SyntaxCode code={stateJson} />
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-3 border-t border-[#16222e] px-4 py-2.5">
        <p className="min-w-0 flex-1 truncate text-[11px] text-[#6e8599]">{spec.summary}</p>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 rounded border border-[#24384a] px-2.5 py-1 text-[11px] text-[#9db3c7] transition-colors hover:border-[#3a5a74] hover:text-[#cfe3f5]"
        >
          {copied ? "已复制" : "复制代码"}
        </button>
      </div>
    </section>
  );
}
